import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { CallOrigin } from "@/types";
import { RootState } from "@/store";
import { CallingLeadResult } from "@/server/data/calling-lead";

export type CallMode = "READY" | "PREPARING" | "INCOMING" | "RING" | "IN_CALL" | "CALL_ENDED";

export interface CallState {
  mode: CallMode;
  callType?: string;
  /** list / follow_up / manual — shown as a badge in the call bar. */
  callOrigin?: CallOrigin | null;
  leadId?: number;
  phone?: string;
  leadName?: string;
  callStarted?: Date | null;
  callEnded?: Date | null;
  ringStart?: Date | null;
  incomingMsg?: string | null;
  inCallingLeads: CallingLeadResult[];
  inCallingLeadsNumber?: string | null;
  keypadEnabled: boolean;
  callId?: number;
  // Contact info for linking calls to specific contacts
  contactId?: number;
  contactInfoId?: number;
  // Preparing step text shown in CallBar during PREPARING mode
  preparingStep?: string;
  // Pre-selected call type from rule evaluation
  preSelectedCallTypeId?: number | null;
  preSelectedCallTypeName?: string | null;
  preSelectedCallTypeScriptId?: number | null;
  preSelectedCallTypeSource?: "rule" | null;
  preSelectedCallTypeReason?: string | null;
  // Script panel dismissed state (shown as reopen button in call bar)
  scriptDismissed: boolean;
  // True when the recorder failed to start for the active call — drives a
  // persistent "NOT RECORDING" warning so the agent knows audio isn't captured.
  recordingFailed?: boolean;
  // PBX listen/whisper: set while this session is a listen leg on another
  // agent's call (the call bar renders "Mithören" instead of a normal call).
  listenRef?: string | null;
}

const initialState: CallState = {
  mode: "READY",
  inCallingLeads: [],
  keypadEnabled: false,
  scriptDismissed: false,
  recordingFailed: false,
  listenRef: null,
};

export const CallSlice = createSlice({
  name: "call",
  initialState,
  reducers: {
    reset: (state) => {
      // Reset to initial state but preserve the reference for better performance
      Object.assign(state, initialState);
    },
    setMode: (state, { payload }: PayloadAction<CallMode>) => {
      state.mode = payload;
    },
    setPreparing: (
      state,
      {
        payload,
      }: PayloadAction<{
        leadId: number;
        phone: string;
        leadName: string;
      }>,
    ) => {
      state.mode = "PREPARING";
      state.leadId = payload.leadId;
      state.phone = payload.phone;
      state.leadName = payload.leadName;
      state.preparingStep = undefined;
    },
    setPreparingStep: (state, { payload }: PayloadAction<string>) => {
      if (state.mode === "PREPARING") {
        state.preparingStep = payload;
      }
    },
    setIncoming: (
      state,
      {
        payload,
      }: PayloadAction<{
        msg: string;
        phone: string;
        leads: CallingLeadResult[];
        leadId?: number;
        leadName?: string;
        contactId?: number;
        contactInfoId?: number;
      }>,
    ) => {
      state.mode = "INCOMING";
      state.incomingMsg = payload.msg;
      state.phone = payload.phone;
      state.inCallingLeads = payload.leads;
      state.inCallingLeadsNumber = payload.phone;
      state.leadId = payload.leadId;
      state.leadName = payload.leadName;
      state.contactId = payload.contactId;
      state.contactInfoId = payload.contactInfoId;
      // Set ringStart for incoming calls to calculate ring duration
      state.ringStart = new Date();
    },
    updateIncomingDetails: (
      state,
      {
        payload,
      }: PayloadAction<{
        msg: string;
        leads: CallingLeadResult[];
        leadId?: number;
        leadName?: string;
        contactId?: number;
        contactInfoId?: number;
      }>,
    ) => {
      // Only update if we're still in INCOMING mode
      if (state.mode === "INCOMING") {
        state.incomingMsg = payload.msg;
        state.inCallingLeads = payload.leads;
        state.leadId = payload.leadId;
        state.leadName = payload.leadName;
        state.contactId = payload.contactId;
        state.contactInfoId = payload.contactInfoId;
      }
    },
    setRing: (
      state,
      {
        payload,
      }: PayloadAction<{
        leadId: number;
        phone: string;
        leadName: string;
        contactId?: number;
        contactInfoId?: number;
        listenRef?: string;
        callOrigin?: CallOrigin;
      }>,
    ) => {
      state.mode = "RING";
      state.ringStart = new Date();
      state.leadId = payload.leadId;
      state.phone = payload.phone;
      state.leadName = payload.leadName;
      state.contactId = payload.contactId;
      state.contactInfoId = payload.contactInfoId;
      state.listenRef = payload.listenRef ?? null;
      state.callOrigin = payload.callOrigin ?? null;
    },
    setInCall: (state) => {
      state.mode = "IN_CALL";
      state.callStarted = new Date();
      state.scriptDismissed = false;
    },
    setScriptDismissed: (state, { payload }: PayloadAction<boolean>) => {
      state.scriptDismissed = payload;
    },
    setRecordingFailed: (state, { payload }: PayloadAction<boolean>) => {
      state.recordingFailed = payload;
    },
    toggleKeypad: (state) => {
      state.keypadEnabled = !state.keypadEnabled;
    },
    setCallId: (state, { payload }: PayloadAction<number>) => {
      state.callId = payload;
    },
    setCallEnded: (state) => {
      state.mode = "CALL_ENDED";
      state.callEnded = new Date();
    },
    setPreSelectedCallType: (
      state,
      { payload }: PayloadAction<{ id: number; name: string; scriptId: number | null; source?: "rule" | null; reason?: string | null } | null>,
    ) => {
      state.preSelectedCallTypeId = payload?.id ?? null;
      state.preSelectedCallTypeName = payload?.name ?? null;
      state.preSelectedCallTypeScriptId = payload?.scriptId ?? null;
      state.preSelectedCallTypeSource = payload?.source ?? null;
      state.preSelectedCallTypeReason = payload?.reason ?? null;
    },
    resetCall: (state) => {
      state.mode = "READY";
      state.callType = undefined;
      state.callOrigin = null;
      state.leadId = undefined;
      state.phone = undefined;
      state.leadName = undefined;
      state.callStarted = null;
      state.callEnded = null;
      state.ringStart = null;
      state.incomingMsg = null;
      state.inCallingLeads = [];
      state.inCallingLeadsNumber = null;
      state.keypadEnabled = false;
      state.callId = undefined;
      state.contactId = undefined;
      state.contactInfoId = undefined;
      state.preparingStep = undefined;
      state.preSelectedCallTypeId = null;
      state.preSelectedCallTypeName = null;
      state.preSelectedCallTypeScriptId = null;
      state.preSelectedCallTypeSource = null;
      state.preSelectedCallTypeReason = null;
      state.scriptDismissed = false;
      state.listenRef = null;
    },
    // Select a specific lead from multiple found leads (for incoming calls)
    // Only allowed during INCOMING or RING — once in IN_CALL, leadId is locked.
    selectLeadFromIncoming: (
      state,
      {
        payload,
      }: PayloadAction<{
        leadId: number;
        leadName: string;
        contactId?: number;
        contactInfoId?: number;
      }>,
    ) => {
      if (state.mode !== "INCOMING" && state.mode !== "RING") return;
      state.leadId = payload.leadId;
      state.leadName = payload.leadName;
      state.contactId = payload.contactId;
      state.contactInfoId = payload.contactInfoId;
    },
  },
});

export const {
  setIncoming,
  updateIncomingDetails,
  setPreparing,
  setPreparingStep,
  setRing,
  setInCall,
  toggleKeypad,
  setCallId,
  setCallEnded,
  resetCall,
  selectLeadFromIncoming,
  setPreSelectedCallType,
  setScriptDismissed,
  setRecordingFailed,
} = CallSlice.actions;

export const selectCall = (state: RootState) => state.call;
